import { useEffect, useId, useRef, useState } from "react";
import type { RoleLevelPreference, TimeRange, VisitFilter } from "../types/job";
import { ROLE_LEVEL_OPTIONS } from "../lib/roleLevel";

const TIME_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: "24h", label: "24 hrs" },
  { value: "7d", label: "7 days" },
];

const VISIT_OPTIONS: { value: VisitFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "visited", label: "Applied" },
  { value: "not_visited", label: "Not applied" },
];

const ROLE_FILTER_OPTIONS: { value: RoleLevelPreference; label: string }[] = [
  { value: "all", label: "All levels" },
  ...ROLE_LEVEL_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

type FilterMenuProps = {
  timeRange: TimeRange;
  onTimeRangeChange: (v: TimeRange) => void;
  visitFilter: VisitFilter;
  onVisitFilterChange: (v: VisitFilter) => void;
  roleLevel: RoleLevelPreference;
  onRoleLevelChange: (v: RoleLevelPreference) => void;
};

function selectClass() {
  return "h-9 w-full cursor-pointer appearance-none rounded-lg border border-slate-200 bg-slate-50/80 py-1.5 pl-2.5 pr-8 text-sm font-medium text-slate-800 transition hover:border-slate-300 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20";
}

export function FilterMenu({
  timeRange,
  onTimeRangeChange,
  visitFilter,
  onVisitFilterChange,
  roleLevel,
  onRoleLevelChange,
}: FilterMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const hasNonDefaultFilters = timeRange !== "7d" || visitFilter !== "all";

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <button
        type="button"
        className="relative inline-flex h-10 items-center gap-2 rounded-lg border-2 border-primary bg-white px-3 text-sm font-medium text-slate-800 shadow-sm transition hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={menuId}
        onClick={() => setOpen((o) => !o)}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          className="shrink-0 text-primary"
          aria-hidden
        >
          <path
            d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="pr-0.5">Filters</span>
        {hasNonDefaultFilters ? (
          <span
            className="absolute right-2 top-1.5 size-2 rounded-full bg-primary ring-2 ring-white"
            aria-hidden
          />
        ) : null}
      </button>

      {open ? (
        <div
          id={menuId}
          role="dialog"
          aria-label="Job filters"
          className="absolute right-0 top-[calc(100%+0.5rem)] z-[60] w-[min(calc(100vw-2rem),19rem)] rounded-xl border border-slate-200 bg-white p-3 shadow-lg"
        >
          <div className="space-y-3">
            <div>
              <label htmlFor="filter-role" className="text-xs font-semibold text-slate-500">
                Role focus
              </label>
              <div className="relative mt-1">
                <select
                  id="filter-role"
                  value={roleLevel}
                  onChange={(e) =>
                    onRoleLevelChange(e.target.value as RoleLevelPreference)
                  }
                  className={selectClass()}
                >
                  {ROLE_FILTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-500">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="filter-posted" className="text-xs font-semibold text-slate-500">
                Posted
              </label>
              <div className="relative mt-1">
                <select
                  id="filter-posted"
                  value={timeRange}
                  onChange={(e) => onTimeRangeChange(e.target.value as TimeRange)}
                  className={selectClass()}
                >
                  {TIME_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-500">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="filter-visit" className="text-xs font-semibold text-slate-500">
                Job listings
              </label>
              <div className="relative mt-1">
                <select
                  id="filter-visit"
                  value={visitFilter}
                  onChange={(e) => onVisitFilterChange(e.target.value as VisitFilter)}
                  className={selectClass()}
                >
                  {VISIT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-500">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
